// Shared, styled dialog primitives — replace the native window.prompt() /
// confirm() / alert() calls that break the glass design system, aren't
// translatable, and aren't keyboard/AX-friendly. All use the tk-modal pattern,
// close on Escape, and trap initial focus.

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { X, AlertTriangle } from 'lucide-react';

function Overlay({ onClose, children }: { onClose: () => void; children: ReactNode }) {
  // Escape closes; focus the panel on mount (basic focus management).
  const panelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    panelRef.current?.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="tk-modal-overlay" onClick={onClose}>
      <div
        ref={panelRef}
        tabIndex={-1}
        className="tk-modal w-full max-w-md p-5 outline-none"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        {children}
      </div>
    </div>
  );
}

/** Styled confirm — replaces window.confirm(). `tone="critical"` for destructive. */
export function ConfirmDialog({
  title, message, confirmLabel, cancelLabel, tone = 'critical', busy = false, onConfirm, onClose,
}: {
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: 'critical' | 'primary';
  busy?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation('components');
  return (
    <Overlay onClose={onClose}>
      <div className="mb-3 flex items-start gap-3">
        {tone === 'critical' && <AlertTriangle size={18} className="mt-0.5 shrink-0 text-critical" />}
        <div className="min-w-0">
          <h2 className="text-base font-semibold">{title}</h2>
          <p className="mt-1 text-sm text-muted">{message}</p>
        </div>
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <button className="tk-btn-ghost" onClick={onClose} disabled={busy}>{cancelLabel ?? t('dialogs.cancel')}</button>
        <button
          className={tone === 'critical' ? 'tk-btn-primary bg-critical' : 'tk-btn-primary'}
          onClick={onConfirm} disabled={busy} autoFocus
        >{busy ? t('dialogs.working') : (confirmLabel ?? t('dialogs.confirm'))}</button>
      </div>
    </Overlay>
  );
}

/** Styled single-text-field prompt — replaces window.prompt(). */
export function PromptDialog({
  title, label, placeholder, defaultValue = '', confirmLabel, onSubmit, onClose,
}: {
  title: string;
  label?: string;
  placeholder?: string;
  defaultValue?: string;
  confirmLabel?: string;
  onSubmit: (value: string) => void | Promise<void>;
  onClose: () => void;
}) {
  const { t } = useTranslation('components');
  const [value, setValue] = useState(defaultValue);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => { inputRef.current?.focus(); inputRef.current?.select(); }, []);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!value.trim()) return;
    setBusy(true);
    try { await onSubmit(value.trim()); onClose(); } finally { setBusy(false); }
  };
  return (
    <Overlay onClose={onClose}>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-base font-semibold">{title}</h2>
        <button className="tk-btn-ghost tk-icon-btn" onClick={onClose} aria-label={t('dialogs.close')}><X size={16} /></button>
      </div>
      <form onSubmit={submit} className="space-y-3">
        {label && <label className="block text-sm text-muted">{label}</label>}
        <input ref={inputRef} className="tk-input" value={value} onChange={(e) => setValue(e.target.value)} placeholder={placeholder} />
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" className="tk-btn-ghost" onClick={onClose} disabled={busy}>{t('dialogs.cancel')}</button>
          <button type="submit" className="tk-btn-primary" disabled={busy || !value.trim()}>{busy ? t('dialogs.working') : (confirmLabel ?? t('dialogs.ok'))}</button>
        </div>
      </form>
    </Overlay>
  );
}

/** Styled choose-from-list — replaces "type the number of the parent" prompts. */
export function PickerDialog<T extends { id: string; label: string; hint?: string }>({
  title, options, confirmLabel, onPick, onClose,
}: {
  title: string;
  options: T[];
  confirmLabel?: string;
  onPick: (option: T) => void | Promise<void>;
  onClose: () => void;
}) {
  const { t } = useTranslation('components');
  const [selected, setSelected] = useState<string>(options[0]?.id ?? '');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const opt = options.find((o) => o.id === selected);
    if (!opt) return;
    setBusy(true);
    try { await onPick(opt); onClose(); } finally { setBusy(false); }
  };
  return (
    <Overlay onClose={onClose}>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-base font-semibold">{title}</h2>
        <button className="tk-btn-ghost tk-icon-btn" onClick={onClose} aria-label={t('dialogs.close')}><X size={16} /></button>
      </div>
      <div className="max-h-72 space-y-1 overflow-y-auto">
        {options.map((o) => (
          <label key={o.id} className="flex cursor-pointer items-start gap-2 rounded-md p-2 text-sm ring-1 ring-white/10 hover:bg-white/5">
            <input type="radio" name="picker" className="mt-0.5" checked={selected === o.id} onChange={() => setSelected(o.id)} />
            <span className="min-w-0">
              <span className="block font-medium">{o.label}</span>
              {o.hint && <span className="block text-xs text-muted">{o.hint}</span>}
            </span>
          </label>
        ))}
      </div>
      <div className="flex justify-end gap-2 pt-3">
        <button className="tk-btn-ghost" onClick={onClose} disabled={busy}>{t('dialogs.cancel')}</button>
        <button className="tk-btn-primary" onClick={submit} disabled={busy || !selected}>{busy ? t('dialogs.working') : (confirmLabel ?? t('dialogs.select'))}</button>
      </div>
    </Overlay>
  );
}
